import { FC, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { defineMessages, useIntl } from 'react-intl';
import { TrackingApi } from 'src/api';
import { FollowLocalsTrackingAction } from 'src/api/tracking/types';
import { Icon } from 'src/components';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { isNotProduction } from 'src/utils/env/isNotProduction';
import { LOCALS } from './locals';
import { followLocalsFeatureFlags } from './followLocalsFeatureFlags';
import { ShownSessionVideo, videoThumbnailSrc } from './sessionVideos';
import * as S from './styles';

const msgs = defineMessages({
  pageTitle: {
    id: 'follow_locals_page_title',
    defaultMessage: 'Follow your favorite locals',
  },
  subtitle: {
    id: 'follow_locals_subtitle',
    defaultMessage: "Tap the speakers you've met to see more videos from them.",
  },
  keepLearning: {
    id: 'follow_locals_keep_learning',
    defaultMessage: 'Keep learning to meet more locals',
  },
  continue: {
    id: 'follow_locals_continue',
    defaultMessage: 'Continue',
  },
  selectedContinue: {
    id: 'follow_locals_selected_continue',
    defaultMessage: '{count} selected, continue',
  },
  notNow: {
    id: 'follow_locals_not_now',
    defaultMessage: 'Not now',
  },
  unnamedLocal: {
    id: 'follow_locals_unnamed_local',
    defaultMessage: 'Speaker',
  },
});

const PORTRAIT_TILTS = [-4, 3, -2, 4, -3];

interface DisplayLocal {
  key: string;
  name: string;
  city: string | null;
  fact: string | null;
  photo: string;
  still: boolean;
  tilt: number;
}

export interface FollowLocalsProps {
  onContinue?: (followedKeys: string[]) => void;
  onNotNow?: () => void;
  /** Videos from the Learn session that just finished. Their still frames are the portraits. */
  sessionVideos?: readonly ShownSessionVideo[];
}

export const FollowLocals: FC<FollowLocalsProps> = ({
  onContinue = () => undefined,
  onNotNow = () => undefined,
  sessionVideos,
}) => {
  const intl = useIntl();
  const { user } = useUserContext();
  const [followed, setFollowed] = useState<ReadonlySet<string>>(() => new Set());
  const reportedFollows = useRef(new Set<string>());
  const locals: DisplayLocal[] = sessionVideos
    ? sessionVideos.map((video, index) => ({
        key: video.actorId != null ? `actor-${video.actorId}` : video.src,
        name: video.actorName ?? intl.formatMessage(msgs.unnamedLocal),
        city: video.accent,
        fact: null,
        photo: videoThumbnailSrc(video.src),
        still: true,
        tilt: PORTRAIT_TILTS[index % PORTRAIT_TILTS.length],
      }))
    : LOCALS.map(local => ({
        key: local.key,
        name: intl.formatMessage(local.name),
        city: intl.formatMessage(local.city),
        fact: intl.formatMessage(local.fact),
        photo: local.photo,
        still: false,
        tilt: local.tilt,
      }));

  const track = (action: FollowLocalsTrackingAction, trackingData: Record<string, string> = {}) => {
    void TrackingApi.trackUserAction(
      action,
      'follow_locals_screen',
      getLanguagePairId(),
      !!user?.isPro,
      trackingData,
    );
  };

  useEffect(() => {
    track('screen_viewed');
    locals.forEach(local => {
      track('local_shown', { local: local.key });
    });
    // Once per visit. The speaker list does not change while the screen is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key: string) => {
    if (!followed.has(key) && !reportedFollows.current.has(key)) {
      reportedFollows.current.add(key);
      track('local_followed', { local: key });
    }
    setFollowed(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectedCount = followed.size;
  const continueLabel =
    selectedCount > 0
      ? intl.formatMessage(msgs.selectedContinue, { count: selectedCount })
      : intl.formatMessage(msgs.continue);

  return (
    <S.Screen data-testid="follow-locals">
      <S.Logo aria-hidden="true">
        <Icon name="memrise" />
      </S.Logo>
      <S.Body>
        <S.Header>
          <S.Title>{intl.formatMessage(msgs.pageTitle)}</S.Title>
          <S.Subtitle>{intl.formatMessage(msgs.subtitle)}</S.Subtitle>
          <S.Rule />
        </S.Header>
        <S.Grid>
          {locals.map(local => {
            const selected = followed.has(local.key);
            return (
              <S.LocalButton
                key={local.key}
                type="button"
                aria-pressed={selected}
                onClick={() => toggle(local.key)}
              >
                <S.Portrait $tilt={local.tilt}>
                  <S.Ring $selected={selected} />
                  <S.Photo>
                    {local.still ? (
                      <video
                        src={local.photo}
                        muted
                        playsInline
                        preload="metadata"
                        aria-hidden="true"
                        data-testid="follow-local-still"
                      />
                    ) : (
                      <img src={local.photo} alt="" />
                    )}
                  </S.Photo>
                  <S.Chip $selected={selected} $tilt={local.tilt}>
                    <Icon name={selected ? 'check' : 'plus'} />
                  </S.Chip>
                </S.Portrait>
                <S.Copy>
                  <S.Name>{local.name}</S.Name>
                  {local.city && <S.City>{local.city}</S.City>}
                  {local.fact && <S.Fact>{local.fact}</S.Fact>}
                </S.Copy>
              </S.LocalButton>
            );
          })}
          <S.LockedCard>
            <S.LockedPortrait>
              <Icon name="padlock" />
            </S.LockedPortrait>
            <S.LockedLabel>{intl.formatMessage(msgs.keepLearning)}</S.LockedLabel>
          </S.LockedCard>
        </S.Grid>
      </S.Body>
      <S.Footer>
        <S.NotNowButton
          buttonType="tertiary"
          buttonSize="large"
          data-testid="follow-locals-not-now"
          onClick={() => {
            track('clicked_not_now');
            onNotNow();
          }}
        >
          {intl.formatMessage(msgs.notNow)}
        </S.NotNowButton>
        <S.ContinueWrap>
          <S.ContinueButton
            buttonType="primary"
            buttonSize="large"
            data-testid="follow-locals-continue"
            onClick={() => {
              const followedKeys = Array.from(followed);
              track('clicked_continue', {
                followed_count: String(followedKeys.length),
                followed_locals: followedKeys.join(','),
              });
              onContinue(followedKeys);
            }}
          >
            {continueLabel}
          </S.ContinueButton>
        </S.ContinueWrap>
      </S.Footer>
    </S.Screen>
  );
};

const FollowLocalsPage: FC = () => {
  const intl = useIntl();

  if (!followLocalsFeatureFlags.enabledOutsideProduction || !isNotProduction()) {
    return null;
  }

  return (
    <>
      <Head>
        <title>{`${intl.formatMessage(msgs.pageTitle)} - Memrise`}</title>
      </Head>
      <FollowLocals />
    </>
  );
};

export default FollowLocalsPage;
